import { useBunja } from 'bunja/react';
import { isAfter } from 'date-fns';
import { useAtomValue, useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';

import { leverageRestrictionBunja } from '@flipster/features/src/margin-bonus/leverage-restriction.state';
import { fireYourPositionsManageClickEvent } from '@flipster/features/src/position-close-modal/segment';
import { useClosePositionModal } from '@flipster/features/src/position-close-modal/use-close-position-modal';
import { calcFlipEstimatedEntryPrice } from '@flipster/features/src/shared/calculator/instant-flip';
import { useClosePosition } from '@flipster/features/src/shared/trade-actions';
import { useTradingPause } from '@flipster/features/src/shared/trading-pause/state';
import { TradingPauseTooltipIcon, getTooltipPosition } from '@flipster/features/src/shared/trading-pause/tooltip-icon';
import { tradingSessionCheckBunja } from '@flipster/features/src/shared/trading-suspension/state';
import { useOverlay } from '@flipster/overlay';
import { getPositionDisplayPrice } from '@flipster/r230522/original/ticker-price';
import { Button } from '@flipster/react/design-system/button';
import { Checkbox } from '@flipster/react/design-system/checkbox';
import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import { IconButton } from '@flipster/react/design-system/icon-button';
import { CountdownDate } from '@flipster/react/design-utils/countdown-date';
import CautionCircleIcon from '@flipster/react/icon/generated/CautionCircleIcon';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { css } from '@flipster/styled-system/css';
import { hstack, vstack } from '@flipster/styled-system/patterns';
import { TableCell } from '@flipster/ui/Table';
import { convertDate } from '@flipster/utils/datetime';
import { getFlipsterConfigsV1Key, getLocalStorage, setLocalStorage } from '@flipster/utils/localStorage';
import segment from '@flipster/utils/segment';
import { ButtonText } from '@flipster/utils/segment/events/position';
import { ProfitBoostEvent } from '@flipster/utils/segment/events/profit-boost';

import CloseAirdropPositionModal from '@web/app/[locale]/(web)/_modal/CloseAirdropPositionModal';
import { perpAccountBunja } from '@web/app/[locale]/(web)/trade/_misc/order-form/perpetual/bunja/base';
import { useInstantFlipModal } from '@web/features/modals/InstantFlipModal/useInstantFlipModal';

import { yourPositionsBunja } from '../../../../state';
import { pendingPositionTableBunja } from '../../../state';
import { shouldAlignAirdropCtaToCloseColumn } from '../../cta-layout';
import { useCtaWidth } from '../../cta-width-context';
import { openPositionRowDataBunja } from '../row-bunja';
import { useOpenPositionSnapshot } from '../use-open-position-snapshot';

interface Props {
  index: number;
}

/**
 * CTA 핸들러들이 실행 시점에 읽는 source 스냅샷 — 구독하지 않으므로 tick에 리렌더를 만들지 않는다.
 * 공용 필드는 useOpenPositionSnapshot에서 가져오고, CTA 전용 isOneWayMode(flip segment 로깅용)만 얹는다.
 */
const useCtaSnapshot = () => {
  const readCommon = useOpenPositionSnapshot();
  const { isOneWayAtom } = useBunja(perpAccountBunja);

  return useAtomCallback(
    useCallback((get) => ({ ...readCommon(), isOneWayMode: get(isOneWayAtom) }), [readCommon, isOneWayAtom]),
  );
};

/** 일반 포지션 CTA — 표시는 sideAtom(플립 라벨)만 구독, 핸들러는 클릭 시점 스냅샷 */
const NormalCtaButtons = ({ index }: { index: number }) => {
  const transCommon = useTranslations('Common.r250401');
  const transAssets = useTranslations('Assets.r250401');
  const transPositionProfitBoost = useTranslations('Position_profit_boost.r250401');

  const { symbol, slot, sideAtom } = useBunja(openPositionRowDataBunja);
  const side = useAtomValue(sideAtom);
  const flipSide = side === 'Long' ? 'Short' : 'Long';

  // 스코프(SymbolScope)는 행 Provider에서 주입된다
  const { checkTradable, guardOffHoursAlert } = useBunja(tradingSessionCheckBunja);
  const { positionTabTypeAtom } = useBunja(yourPositionsBunja);
  const { pendingPositionTypeAtom } = useBunja(pendingPositionTableBunja);
  const setPositionTabType = useSetAtom(positionTabTypeAtom);
  const setPendingPositionType = useSetAtom(pendingPositionTypeAtom);
  const { leverageRestrictionAtom } = useBunja(leverageRestrictionBunja);
  const { isMaxInitLeverageRestricted } = useAtomValue(leverageRestrictionAtom);
  const { isPaused } = useTradingPause(symbol);
  const readSnapshot = useCtaSnapshot();

  const onMarketCloseComplete = () => {
    setPositionTabType('open');
  };
  const onLimitCloseComplete = () => {
    setPositionTabType('pending');
    setPendingPositionType('limit');
  };

  const { onOpenInstantFlipModal, isFlipLoading, instantFlip } = useInstantFlipModal();
  const { closeFullPosition, isCloseLoading: isLoading } = useClosePosition({ onMarketCloseComplete });
  const { onOpenFullCloseModal, onOpenPartialCloseModal } = useClosePositionModal({
    onMarketCloseComplete,
    onLimitCloseComplete,
  });

  const calculatedPosition = getTooltipPosition(index);
  const { reportRef } = useCtaWidth();
  const buttonsRefCallback = useCallback(
    (el: HTMLDivElement | null) => {
      reportRef(`${symbol}_${slot}`, el);
    },
    [reportRef, symbol, slot],
  );

  const flipPosition = () => {
    const { position, account, isOneWayMode, showAllSymbol, user } = readSnapshot();
    if (!position || !user) return;
    const snapshotFlipSide = position.side === 'Long' ? 'Short' : 'Long';
    segment.track(ProfitBoostEvent.InstantFlipClick, {
      event_location: 'order_form_page',
      position_id: position.positionId,
      profit_boost_mode: Boolean(position.boostExpiryTimestamp),
      position_mode: isOneWayMode ? 'oneway' : 'multiposition',
      symbol: position.symbol,
      leverage: Number(position.initLeverage),
      current_price: Number(getPositionDisplayPrice(position)),
      entry_price: Number(position.avgEntryPrice),
      mark_price: Number(position.markPrice),
      liquidation_price: Number(position.liquidationPrice),
      side: position.side === 'Long' ? 'buy' : 'sell',
      margin_usdt: Number(position.fundsInvested),
      unrealized_pnl: Number(position.unrealizedPnl),
      unrealized_pnl_percentage: Number(position.calcUnrealizedPnlPercent),
    });
    const key = getFlipsterConfigsV1Key(user.userId, 'skipInstantFlipModal');
    if (getLocalStorage(key)) {
      instantFlip({
        symbol,
        slot,
        side: snapshotFlipSide,
        price: calcFlipEstimatedEntryPrice(position),
      });
    } else {
      onOpenInstantFlipModal(position.symbol, position.slot);
    }
    if (!account) return;
    fireYourPositionsManageClickEvent(`Flip to ${snapshotFlipSide.toLowerCase()}` as ButtonText, {
      ...position,
      mode: account.tradeMode,
      isShowAll: showAllSymbol,
      isProfitBoostMode: Boolean(position.boostExpiryTimestamp),
    });
  };

  const handleFlip = () => {
    const { position, user } = readSnapshot();
    if (!position) return;
    const isBoosting = position.boostExpiryTimestamp !== null;
    let isChecked = false;

    const storageKey = user && getFlipsterConfigsV1Key(user.userId, 'skipFlipWarningInProfitBoost');
    const canSkip = (storageKey && (getLocalStorage(storageKey) as boolean)) || false;

    if (isBoosting && !canSkip) {
      dialog.open({
        title: {
          label: (
            <div className={hstack({ gap: 4 })}>
              <CautionCircleIcon size={24} className={css({ color: 'content.state.warning' })} />
              {transPositionProfitBoost('Flip_position_warning_title')}
            </div>
          ),
        },
        content: (
          <div className={vstack({ gap: 16, alignItems: 'flex-start' })}>
            <div>{transPositionProfitBoost('Flip_position_warning_description')}</div>
            <Checkbox
              onChange={(e) => {
                isChecked = e.target.checked;
              }}
              css={{ color: 'content.default.level-2' }}
            >
              {transCommon('Dont_show_warning_again')}
            </Checkbox>
          </div>
        ),
        buttons: {
          primary: (
            <Dialog.Button
              hierarchy='primary'
              label={transCommon('Confirm')}
              onClick={() => {
                segment.track(ProfitBoostEvent.ProfitBoostFlipPositionConfirmClick, {
                  position_id: position.positionId,
                  event_location: 'order_form_page',
                });
                if (isChecked && storageKey) setLocalStorage(storageKey, true);
                flipPosition();
              }}
            />
          ),
          secondary: <Dialog.Button hierarchy='secondary' color='neutral' label={transCommon('Cancel')} />,
        },
      });
    } else {
      flipPosition();
    }
  };

  return (
    // z-index : profit boost gradient border animation
    <div ref={buttonsRefCallback} className={hstack({ gap: 8 })}>
      <TradingPauseTooltipIcon symbol={symbol} position={calculatedPosition} />
      <Button
        hierarchy='secondary'
        color='neutral'
        size='small'
        isLoading={isLoading}
        disabled={isPaused}
        onClick={() => {
          if (!checkTradable()) return;
          const { position, account } = readSnapshot();
          if (!position) return;
          onOpenPartialCloseModal(position, account?.tradeMode);
        }}
        label={transAssets('Partial_close')}
        withSpinner
        css={{ flexShrink: 0 }}
      />
      <Button
        hierarchy='secondary'
        color='neutral'
        size='small'
        isLoading={isLoading}
        disabled={isPaused}
        onClick={() => {
          if (!checkTradable()) return;
          const { position, account, user } = readSnapshot();
          if (!position || !user) return;
          const key = getFlipsterConfigsV1Key(user.userId, 'skipPositionFullCloseModal');
          if (getLocalStorage(key)) {
            closeFullPosition({ position });
          } else {
            onOpenFullCloseModal(position, account?.tradeMode);
          }
        }}
        label={transAssets('Full_close')}
        withSpinner
        css={{ flexShrink: 0 }}
      />
      <Button
        hierarchy='secondary'
        color='accent'
        size='small'
        isLoading={isFlipLoading}
        disabled={isMaxInitLeverageRestricted || isPaused}
        onClick={async () => {
          if (!checkTradable()) return;
          if (!(await guardOffHoursAlert('flip'))) return;
          handleFlip();
        }}
        label={flipSide === 'Long' ? transAssets('Flip_to_long') : transAssets('Flip_to_short')}
        withSpinner
        css={{ flexShrink: 0 }}
      />
    </div>
  );
};

/** airdrop 포지션 CTA — lockedUntil(사실상 static)만 구독, 잠금 해제는 CountdownDate onEnd로 전환 */
const AirdropCta = () => {
  const transCommon = useTranslations('Common.r250401');
  const transAssets = useTranslations('Assets.r250401');

  const { symbol, slot, lockedUntilAtom } = useBunja(openPositionRowDataBunja);
  const lockedUntil = useAtomValue(lockedUntilAtom);
  const lockedUntilDate = convertDate(lockedUntil) ?? new Date();

  // 카운트다운이 끝났다는 표시. CountdownDate onEnd로만 true가 되며, 이때 리렌더되어 청산 버튼으로 전환된다.
  const [isCountdownEnded, setIsCountdownEnded] = useState(false);
  // 동일 slot에 airdrop이 재지급돼 잠금 시각이 미래로 갱신되면, 이전 "종료" 표시는 더 이상 유효하지 않다.
  // → lockedUntil이 바뀌면 종료 표시를 초기화해 새 잠금 기준으로 다시 카운트다운한다.
  useEffect(() => {
    setIsCountdownEnded(false);
  }, [lockedUntil]);

  const isAirdropTradable = isCountdownEnded || isAfter(new Date(), lockedUntilDate);

  const { checkTradable } = useBunja(tradingSessionCheckBunja);
  const { positionTabTypeAtom } = useBunja(yourPositionsBunja);
  const setPositionTabType = useSetAtom(positionTabTypeAtom);
  const readSnapshot = useCtaSnapshot();
  const overlay = useOverlay();
  const { closeFullPosition } = useClosePosition({ onMarketCloseComplete: () => setPositionTabType('open') });

  const { reportRef, hasFlippablePosition, maxWidth } = useCtaWidth();
  const buttonsRefCallback = useCallback(
    (el: HTMLDivElement | null) => {
      reportRef(`${symbol}_${slot}`, el);
    },
    [reportRef, symbol, slot],
  );

  // 혼합(일반 + airdrop) 케이스에서만 airdrop close 버튼을 'Close' 헤더 아래에 맞춘다.
  const alignAirdropCtaToCloseColumn = shouldAlignAirdropCtaToCloseColumn({ isAirdrop: true, hasFlippablePosition });

  return (
    // flexShrink:0 + width:max-content 로 래퍼가 줄지 않게 해 버튼이 항상 intrinsic 폭을 유지(모든 언어에서 라벨 ellipsis 방지).
    // reportRef로 그 전체 폭을 보고해 close-flip 컬럼이 minWidth를 확보한다.
    // 혼합 케이스에서는 래퍼를 maxWidth 박스로 넓혀 그 안에서 좌측 정렬 → airdrop close 버튼이 'Close' 헤더 아래에 정렬된다.
    <div
      ref={buttonsRefCallback}
      className={hstack({ flexShrink: 0, justifyContent: 'flex-start', width: 'max-content' })}
      style={alignAirdropCtaToCloseColumn ? { width: maxWidth || undefined } : undefined}
    >
      {isAirdropTradable ? (
        <Button
          size='small'
          hierarchy='secondary'
          onClick={() => {
            if (!checkTradable()) return;
            const { position, instrument, user } = readSnapshot();
            if (!position || !instrument || !user) return;
            const key = getFlipsterConfigsV1Key(user.userId, 'skipAirdropPositionCloseModal');
            if (getLocalStorage(key)) {
              closeFullPosition({ position });
            } else {
              overlay.open(({ isOpen, close, unmount }) => (
                <CloseAirdropPositionModal
                  isOpen={isOpen}
                  onClose={close}
                  position={position}
                  currency={instrument.currency}
                  qtyPrecision={instrument.tradeQtyPrecision}
                  onExit={unmount}
                />
              ));
            }
          }}
          label={transAssets('Close_your_free_airdrop')}
        />
      ) : (
        <div className={hstack()}>
          <div className={hstack({ gap: 4 })}>
            <span className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>
              {transAssets('Tradable_after')}
            </span>
            <CountdownDate
              targetDate={lockedUntilDate}
              onEnd={() => setIsCountdownEnded(true)}
              css={{ textStyle: 'Text/S/Monospace/Strong', color: 'content.default.level-5' }}
            />
          </div>
          <IconButton
            hierarchy='quaternary'
            icon={<InformationCircleIcon />}
            size='small'
            onClick={() =>
              dialog.open({
                title: {
                  label: transAssets('Position_timer'),
                },
                content: transAssets('Position_timer_bottomsheet_description'),
                buttons: {
                  primary: <Dialog.Button hierarchy='primary' label={transCommon('Ok')} />,
                },
              })
            }
          />
        </div>
      )}
    </div>
  );
};

/** 셸 — 분기용 boolean(isReady/isAirdrop)만 구독. tick에는 리렌더되지 않는다 */
const CtaCell = ({ index }: Props) => {
  const { isReadyAtom, isAirdropAtom } = useBunja(openPositionRowDataBunja);
  const isReady = useAtomValue(isReadyAtom);
  const isAirdrop = useAtomValue(isAirdropAtom);

  if (!isReady) return null;

  return (
    // 셀은 항상 우측 정렬해 헤더의 maxWidth 박스(Close/Flip 라벨)와 같은 영역에 버튼을 얹는다.
    <TableCell align='right' className={css({ zIndex: 2 })}>
      {isAirdrop ? <AirdropCta /> : <NormalCtaButtons index={index} />}
    </TableCell>
  );
};

export default CtaCell;
